import { zodResolver } from '@hookform/resolvers/zod';
import * as zod from 'zod';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Box,
  Button,
  CircularProgress,
  InlineNotification,
  Stack,
  TextField,
  Typography,
} from '@packages/shared';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type {
  GTMEventGlycerinDisplayForm,
  GTMEventGlycerinDisplayFormSuccess,
} from '@packages/tracking/src/types/events';
import { Dialog, type DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';

import { DialogHeader } from '../../Dialog/DialogHeader/DialogHeader.client';
import { usePasswordForget } from '../api/usePasswordForget';

type PasswordForgotDialogProps = DialogProps & {
  handleClose?: () => void;
};

const messages = defineMessages({
  dialogTitle: {
    id: 'Login.label.forgot-password',
    defaultMessage: 'Passwort vergessen?',
  },
  emailLabel: {
    id: 'ForgotPasswordForm.label.email',
    defaultMessage: 'Deine E-Mail-Adresse',
  },
  emailPlaceholder: {
    id: 'ForgotPasswordForm.placeholder.email',
    defaultMessage: 'name@provider.de',
  },
  emailAriaLabel: {
    id: 'ForgotPasswordForm.input.aria-label',
    defaultMessage: 'passwort-vergessen-eingabe',
  },
  submitLabel: {
    id: 'ForgotPasswordForm.label.submit',
    defaultMessage: 'Absenden!',
  },
  closeLabel: {
    id: 'ForgotPasswordSuccess.close',
    defaultMessage: 'Fenster schließen',
  },
  invalidToken: {
    id: 'ForgotPasswordForm.invalidToken',
    defaultMessage:
      'Bitte beachten Sie: Ihre eingegebene URL ist nicht mehr gültig. Bitte fordern Sie einen neuen Link an.',
  },
  introContent: {
    id: 'ForgotPasswordForm.intro',
    defaultMessage: `Passwort ändern,<br></br><br></br>Wir helfen dir, dich wieder anzumelden.<br></br><br></br>Geben Sie hier Ihre E-Mail-Adresse ein. Wir senden Ihnen dann eine Nachricht mit weiteren Anweisungen zum Zurücksetzen Ihres Passwortes zu.`,
  },
  successContent: {
    id: 'ForgotPasswordSuccess.message',
    defaultMessage: `Geschafft!<br></br><br></br>Wenn uns Ihre Mail-Adresse bekannt ist, bekommen Sie in wenigen Minuten eine Nachricht  von uns.<br></br><br></br>
    Klicken Sie einfach auf den Link oder kopieren diesen in Ihren Browser. Danach können Sie ein neues Passwort vergeben. Erst wenn Sie dieses bestätigt haben, wird Ihr altes Passwort gelöscht.<br></br><br></br>
    Sie haben keine E-Mail erhalten? Schauen Sie mal in Ihren Spam-Ordner. Vielleicht ist sie aus Versehen dort gelandet.`,
  },
  error: {
    id: 'ForgotPasswordModal.request.error',
    defaultMessage:
      'Beim Zurücksetzen des Passwortes ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.',
  },
  errorMandatoryField: {
    id: 'ForgotPasswordModal.message.mandatoryField',
    defaultMessage: 'Pflichtfeld',
  },
  errorEmailField: {
    id: 'Login.message.invalidMail',
    defaultMessage: 'Die E-Mail-Adresse ist nicht gültig.',
  },
});

interface FormProps {
  emailAddress: string;
}

export const PasswordForgotDialog = ({ handleClose, ...props }: PasswordForgotDialogProps) => {
  const [submitted, setSubmitted] = useState(false);
  const { formatMessage } = useIntl();
  const { error, loading, passwordForget } = usePasswordForget();
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    dispatchGTMEvent<GTMEventGlycerinDisplayForm>({
      event: 'DisplayForm',
      DisplayFormData: {
        category: 'reset-password',
      },
    });
  }, [dispatchGTMEvent]);
  useEffect(() => {
    if (submitted) {
      dispatchGTMEvent<GTMEventGlycerinDisplayFormSuccess>({
        event: 'DisplayFormSuccess',
        DisplayFormSuccessData: {
          category: 'reset-password',
        },
      });
    }
  }, [dispatchGTMEvent, submitted]);

  const schema = useMemo(
    () =>
      zod.object({
        emailAddress: zod
          .string()
          .min(1, formatMessage(messages.errorMandatoryField))
          .email(formatMessage(messages.errorEmailField)),
      }),
    [formatMessage],
  );

  const closeDialog = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const submitForm: SubmitHandler<FormProps> = async ({ emailAddress }) => {
    const res = await passwordForget(emailAddress);

    if (res) {
      setSubmitted(res);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
  } = useForm<FormProps>({
    resolver: zodResolver(schema),
  });

  return (
    <Dialog {...props} onClose={closeDialog}>
      <DialogHeader onClose={closeDialog}>{formatMessage(messages.dialogTitle)}</DialogHeader>
      <DialogContent>
        <form onSubmit={handleSubmit(submitForm)}>
          {error && <InlineNotification severity="error">{error}</InlineNotification>}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                zIndex: 1,
                inset: '0 0 0 0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.6)',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Stack sx={{ px: 2, py: 4 }} spacing={2}>
            {submitted ? (
              <Typography>{formatMessage(messages.successContent)}</Typography>
            ) : (
              <>
                <Typography>{formatMessage(messages.introContent)}</Typography>
                <Controller
                  control={control}
                  name="emailAddress"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={formatMessage(messages.emailLabel)}
                      placeholder={formatMessage(messages.emailPlaceholder)}
                      error={touchedFields.emailAddress && !!errors.emailAddress?.message}
                      helperText={
                        touchedFields.emailAddress ? errors.emailAddress?.message : undefined
                      }
                      fullWidth
                      inputProps={{
                        'data-testid': 'forgot-password-email',
                        'aria-label': formatMessage(messages.emailAriaLabel),
                      }}
                    />
                  )}
                />
              </>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {submitted ? (
                <Button data-testid="forgot-password-close" onClick={closeDialog} color="primary">
                  {formatMessage(messages.closeLabel)}
                </Button>
              ) : (
                <Button
                  data-testid="forgot-password-submit"
                  type="submit"
                  color="primary"
                  clickTrackingProps={{
                    category: 'reset-password',
                    formId: 'reset-password',
                  }}
                >
                  {formatMessage(messages.submitLabel)}
                </Button>
              )}
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};
